import React, { useState, useMemo, useEffect, useRef } from "react";
import { Button, Form, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FaSearch,
  FaChevronDown,
  FaCalendarAlt,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
} from "react-icons/fa";
import TablePaginationBar from "../components/TablePaginationBar";

/* ─── Styles matching approved UI direction ─── */
const activityTypeStyles = {
  "Initial Assessment": { bg: "#D0FAE5", color: "#006145" }, // Green
  "Follow-Up": { bg: "#E8F1FF", color: "#2F6FED" }, // Blue
  "Care Coordination": { bg: "#F2ECFF", color: "#7A52CC" }, // Purple
  "Case Review": { bg: "#FFF4E5", color: "#D9822B" }, // Orange
  "Contact Attempt": { bg: "#FEE2E2", color: "#991B1B" }, // Pink/Red
  "Patient Deactivated": { bg: "#F3F4F6", color: "#4B5563" },
  "Patient Reactivated": { bg: "#D0FAE5", color: "#006145" },
};

const formatDateToMMDDYYYY = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${m}/${d}/${y}`;
};

const convertDateToYMD = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("/");
  if (parts.length === 3) {
    const [m, d, y] = parts;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return dateStr;
};

const parseDateToMs = (dateStr) => {
  if (!dateStr) return 0;
  return new Date(dateStr).getTime();
};

/* ─── Clinically defined score severity indicators ─── */
const getScoreSeverity = (type, score) => {
  if (score === "-" || score === null || score === undefined) return null;
  const num = parseInt(score, 10);
  if (isNaN(num)) return null;

  if (type === "PHQ-9") {
    if (num <= 4) return { label: "Minimal", bg: "#E8F1FF", color: "#2F6FED" }; // Blue
    if (num <= 9) return { label: "Mild", bg: "#D0FAE5", color: "#006145" }; // Green
    if (num <= 14)
      return { label: "Moderate", bg: "#FEF9E7", color: "#B7950B" }; // Yellow/Orange
    return { label: "Severe", bg: "#FEE2E2", color: "#991B1B" }; // Red
  }
  if (type === "GAD-7") {
    if (num <= 4)
      return { label: "Minimal Anxiety", bg: "#E8F1FF", color: "#2F6FED" };
    if (num <= 9)
      return { label: "Mild Anxiety", bg: "#D0FAE5", color: "#006145" };
    if (num <= 14)
      return { label: "Moderate Anxiety", bg: "#FEF9E7", color: "#B7950B" };
    return { label: "Severe Anxiety", bg: "#FEE2E2", color: "#991B1B" };
  }
  if (type === "SMFQ") {
    if (num <= 7) return { label: "Minimal", bg: "#E8F1FF", color: "#2F6FED" };
    if (num <= 11) return { label: "Mild", bg: "#D0FAE5", color: "#006145" };
    if (num <= 19)
      return { label: "Moderate", bg: "#FEF9E7", color: "#B7950B" };
    return { label: "Severe", bg: "#FEE2E2", color: "#991B1B" };
  }
  if (type === "SCARED") {
    if (num <= 24) return { label: "Minimal", bg: "#E8F1FF", color: "#2F6FED" };
    if (num <= 29) return { label: "Mild", bg: "#D0FAE5", color: "#006145" };
    if (num <= 40)
      return { label: "Moderate", bg: "#FEF9E7", color: "#B7950B" };
    return { label: "Severe", bg: "#FEE2E2", color: "#991B1B" };
  }
  return null;
};

const renderScoreCell = (val) => {
  if (val === "-" || val === null || val === undefined || val === "") {
    return <span style={{ color: "#C5C5C5", fontWeight: 400 }}>—</span>;
  }
  return val;
};

const todayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatDisplayDate = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${m}/${d}/${y}`;
};

const ADD_DRAWER_CONFIG = {
  "Care Coordination": {
    showDuration: true,
    durationPlaceholder: "Enter duration in minutes",
    notesPlaceholder: "Enter coordination notes",
  },
  "Case Review": {
    showDuration: true,
    durationPlaceholder: "Enter duration in minutes",
    notesPlaceholder: "Enter case review notes",
  },
  "Contact Attempt": {
    showDuration: false,
    notesPlaceholder: "Enter outreach notes",
  },
  "Follow-Up": {
    showDuration: true,
    durationPlaceholder: "Enter duration in minutes",
    notesPlaceholder: "Enter follow-up notes",
    requiresInitialAssessment: true,
  },
  "Initial Assessment": {
    showDuration: true,
    durationPlaceholder: "Enter duration in minutes",
    notesPlaceholder: "Enter initial assessment notes",
  },
};

const patientHasInitialAssessment = (patient, activitiesList) => {
  if (patient.initialAssessment) return true;
  return activitiesList.some(
    (a) =>
      a.patientId === patient.id && a.activityType === "Initial Assessment",
  );
};

const drawerFieldLabel = {
  display: "block",
  fontSize: "14px",
  fontWeight: 600,
  color: "#1F2937",
  marginBottom: "8px",
};

const drawerInputStyle = {
  width: "100%",
  border: "none",
  borderRadius: "8px",
  padding: "12px 14px",
  fontSize: "14px",
  color: "#1F2937",
  backgroundColor: "#F3F4F6",
  boxSizing: "border-box",
  outline: "none",
};

/* ─── Patient Search (drawer) ─── */
const DrawerPatientSearch = ({ patients, value, onChange }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = patients.find((p) => p.id === value);
  const filtered = patients.filter(
    (p) =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      (p.dob && p.dob.includes(query)),
  );

  useEffect(() => {
    const handleClick = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        type="text"
        placeholder="Search patient"
        value={
          open
            ? query
            : selected
              ? `${selected.name}${selected.dob ? ` (${selected.dob})` : ""}`
              : ""
        }
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          setQuery("");
        }}
        style={drawerInputStyle}
      />
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            zIndex: 1100,
            maxHeight: "180px",
            overflowY: "auto",
          }}
        >
          {filtered.length === 0 ? (
            <div
              style={{
                padding: "12px 14px",
                fontSize: "13px",
                color: "#9CA3AF",
              }}
            >
              No patients found
            </div>
          ) : (
            filtered.map((p) => (
              <div
                key={p.id}
                onClick={() => {
                  onChange(p.id);
                  setOpen(false);
                  setQuery("");
                }}
                style={{
                  padding: "10px 14px",
                  fontSize: "13px",
                  cursor: "pointer",
                  color: "#374151",
                  backgroundColor: value === p.id ? "#F0FDF4" : "#FFFFFF",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                {p.name}
                {p.dob ? ` (${p.dob})` : ""}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Date picker (drawer) ─── */
const DrawerDateInput = ({ value, onChange }) => {
  const ref = useRef(null);
  return (
    <div
      style={{ position: "relative" }}
      onClick={() => {
        try {
          ref.current?.showPicker();
        } catch {
          ref.current?.focus();
        }
      }}
    >
      <input
        ref={ref}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          position: "absolute",
          opacity: 0,
          width: "100%",
          height: "100%",
          cursor: "pointer",
          zIndex: 2,
        }}
      />
      <div
        style={{
          ...drawerInputStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          color: value ? "#1F2937" : "#9CA3AF",
        }}
      >
        <span>{value ? formatDisplayDate(value) : "Select date"}</span>
        <FaCalendarAlt size={14} style={{ color: "#9CA3AF" }} />
      </div>
    </div>
  );
};

const ActivityCoCM = () => {
  const [search, setSearch] = useState("");
  const [cmFilter, setCmFilter] = useState("All Care Managers");
  const [clinicFilter, setClinicFilter] = useState("All Clinics");
  const [activityTypeFilter, setActivityTypeFilter] =
    useState("All Activity Types");
  const [dateRangeFilter, setDateRangeFilter] = useState("All Time");

  // Custom Dates
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Pagination
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Sorting State
  const [sortColumn, setSortColumn] = useState("dateMs");
  const [sortDirection, setSortDirection] = useState("desc");

  // Drawer State
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [auditComment, setAuditComment] = useState("");
  const [auditStatus, setAuditStatus] = useState("Approved");

  // Data State
  const [patients, setPatients] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);

  const navigate = useNavigate();

  const user = useSelector((state) => state?.user);
  const userRole = user?.user?.role;
  const isCareManager =
    userRole === "careManager" || user?.user?.providerType === "Care Manager";

  // Add Activity Drawer State
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [selectedAddType, setSelectedAddType] = useState("");
  const [addPatientId, setAddPatientId] = useState("");
  const [addDate, setAddDate] = useState("");
  const [addDuration, setAddDuration] = useState("");
  const [addNote, setAddNote] = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);

  const closeAddDrawer = () => {
    setShowAddDrawer(false);
    setSelectedAddType("");
    setAddPatientId("");
    setAddDate("");
    setAddDuration("");
    setAddNote("");
    setEditingActivityId(null);
  };

  const drawerPatients = useMemo(() => {
    const config = ADD_DRAWER_CONFIG[selectedAddType];
    if (!config?.requiresInitialAssessment) return patients;
    return patients.filter((p) => patientHasInitialAssessment(p, activities));
  }, [patients, activities, selectedAddType]);

  const handleOpenAddDrawer = (activityType) => {
    if (activityType === "Initial Assessment") {
      navigate("/activity-cocm/initial-assessment");
      return;
    }
    if (activityType === "Follow-Up") {
      navigate("/activity-cocm/follow-up");
      return;
    }
    setSelectedAddType(activityType);
    setAddPatientId("");
    setAddDate("");
    setAddDuration("");
    setAddNote("");
    setShowAddDrawer(true);
  };

  const handleSaveNewActivity = async (e) => {
    e.preventDefault();
    if (!addPatientId) {
      alert("Please select a patient.");
      return;
    }
    if (!addDate) {
      alert("Please select a date.");
      return;
    }

    const config = ADD_DRAWER_CONFIG[selectedAddType];
    if (config?.showDuration && !addDuration) {
      alert("Please enter duration in minutes.");
      return;
    }

    const selectedPatient = patients.find((p) => p.id === addPatientId);
    if (!selectedPatient) return;

    setAddSaving(true);
    try {
      const token = localStorage.getItem("user_id");
      if (editingActivityId) {
        const response = await axios.put(
          `${process.env.REACT_APP_BASE_URL}/cocm-activities/${editingActivityId}`,
          {
            notes: addNote,
            duration: config?.showDuration
              ? parseInt(addDuration, 10) || 0
              : null,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.data?.success) {
          setActivities((prev) =>
            prev.map((act) =>
              act.id === editingActivityId ? response.data.data : act,
            ),
          );
          closeAddDrawer();
        }
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/cocm-activities`,
          {
            patientId: selectedPatient.id,
            activityType: selectedAddType,
            activityDate: addDate,
            duration: config?.showDuration
              ? parseInt(addDuration, 10) || 0
              : null,
            notes: addNote,
            auditComment: "Self-logged by Care Manager.",
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.data?.success) {
          setActivities((prev) => [response.data.data, ...prev]);
          closeAddDrawer();
        }
      }
    } catch (error) {
      console.error("Error saving activity:", error);
      alert("Failed to save activity. Please try again.");
    } finally {
      setAddSaving(false);
    }
  };

  const fetchActivities = async () => {
    try {
      setLoadingActivities(true);
      const token = localStorage.getItem("user_id");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/cocm-activities`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data?.success) {
        setActivities(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoadingActivities(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/get-patients`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data?.success) {
        const mapped = response.data.data.map((user) => {
          const act1 = parseDateToMs(user.cocmMeta?.lastFollowUp);
          const act2 = parseDateToMs(user.cocmMeta?.lastCaseReview);
          const act3 = parseDateToMs(user.cocmMeta?.initialAssessment);
          const mostRecentActivity = Math.max(act1, act2, act3);

          return {
            id: user._id,
            patientId: user.cocmMeta?.patientId || "",
            name: `${user.firstname} ${user.lastname}`,
            accesscode:
              user.clinicAccessCode ||
              user.cocmMeta?.accessCode?.[0]?.accessCode ||
              user.cocmMeta?.accesscode ||
              "",
            dob: user.dob ? formatDateToMMDDYYYY(user.dob) : "",
            email: user.email,
            phone: user.phoneno,
            status: user.cocmMeta?.status || "Intake",
            clinic: user.cocmMeta?.clinic || "Downtown Health Center",
            cm: user.cocmMeta?.careManager || "",
            initialAssessment: user.cocmMeta?.initialAssessment
              ? formatDateToMMDDYYYY(user.cocmMeta.initialAssessment)
              : "",
            lastFollowUp: user.cocmMeta?.lastFollowUp
              ? formatDateToMMDDYYYY(user.cocmMeta.lastFollowUp)
              : "",
            nextFollowUp: user.cocmMeta?.nextFollowUp
              ? user.cocmMeta.nextFollowUp
              : "",
            lastCaseReview: user.cocmMeta?.lastCaseReview
              ? formatDateToMMDDYYYY(user.cocmMeta.lastCaseReview)
              : "",
            nextCaseReview: user.cocmMeta?.nextCaseReview
              ? user.cocmMeta.nextCaseReview
              : "",
            phq9: user.cocmMeta?.phq9?.latest ?? 0,
            gad7: user.cocmMeta?.gad7?.latest ?? 0,
            smfq: user.cocmMeta?.smfq?.latest ?? 0,
            scared: user.cocmMeta?.scared?.latest ?? 0,
            minutes: user.cocmMeta?.minutes || 0,
            flags: user.cocmMeta?.flags || { flag: false, warning: false },
            mostRecentActivity: mostRecentActivity || 0,
          };
        });
        setPatients(mapped);
      }
    } catch (error) {
      console.error("Error fetching patients:", error);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    fetchActivities();
  }, []);

  // Filter Options List selectors
  const careManagersList = useMemo(() => {
    const list = new Set();
    patients.forEach((p) => {
      if (p.cm) list.add(p.cm);
    });
    return Array.from(list);
  }, [patients]);

  const clinicsList = useMemo(() => {
    const list = new Set();
    patients.forEach((p) => {
      if (p.clinic) list.add(p.clinic);
    });
    return Array.from(list);
  }, [patients]);

  // Handle audit submission
  const handleSaveAudit = async () => {
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/cocm-activities/${selectedActivity.id}`,
        { status: auditStatus, auditComment },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data?.success) {
        setActivities((prev) =>
          prev.map((act) =>
            act.id === selectedActivity.id ? response.data.data : act,
          ),
        );
      }
    } catch (error) {
      console.error("Error saving audit:", error);
    }
    setIsDrawerOpen(false);
  };

  // Open detail drawer
  const handleOpenDrawer = (act) => {
    setSelectedActivity(act);
    setAuditComment(act.auditComment || "");
    setAuditStatus(act.status || "Approved");
    setIsDrawerOpen(true);
  };

  const handleEditClick = () => {
    if (!selectedActivity) return;
    setEditingActivityId(selectedActivity.id);
    setSelectedAddType(selectedActivity.activityType);
    setAddPatientId(selectedActivity.patientId);
    setAddDate(convertDateToYMD(selectedActivity.date));
    setAddDuration(
      selectedActivity.duration === "-" ? "" : selectedActivity.duration,
    );
    setAddNote(selectedActivity.note);
    setShowAddDrawer(true);
    setIsDrawerOpen(false);
  };

  /* ─── Sorting Logic ─── */
  const handleSort = (columnId) => {
    if (sortColumn === columnId) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("desc");
    }
  };

  /* ─── Filter & Sort Activities ─── */
  const filteredAndSortedData = useMemo(() => {
    let result = activities.filter((item) => {
      // 1. Search Query
      const matchesSearch =
        item.patientName.toLowerCase().includes(search.toLowerCase()) ||
        item.careManager.toLowerCase().includes(search.toLowerCase()) ||
        item.clinic.toLowerCase().includes(search.toLowerCase());

      // 2. Care Manager Filter
      const matchesCM =
        cmFilter === "All Care Managers" || item.careManager === cmFilter;

      // 3. Clinic Filter
      const matchesClinic =
        clinicFilter === "All Clinics" || item.clinic === clinicFilter;

      // 4. Activity Type Filter
      const matchesType =
        activityTypeFilter === "All Activity Types" ||
        item.activityType === activityTypeFilter;

      // 5. Date Filter
      let matchesDate = true;
      if (dateRangeFilter !== "All Time") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startOfToday = today.getTime();

        if (dateRangeFilter === "Today") {
          matchesDate = item.dateMs >= startOfToday;
        } else if (dateRangeFilter === "Last 7 Days") {
          matchesDate = item.dateMs >= startOfToday - 7 * 24 * 60 * 60 * 1000;
        } else if (dateRangeFilter === "Last 30 Days") {
          matchesDate = item.dateMs >= startOfToday - 30 * 24 * 60 * 60 * 1000;
        } else if (dateRangeFilter === "Custom Date") {
          const startMs = customStartDate
            ? new Date(customStartDate + "T00:00:00").getTime()
            : 0;
          const endMs = customEndDate
            ? new Date(customEndDate + "T23:59:59").getTime()
            : Infinity;
          matchesDate = item.dateMs >= startMs && item.dateMs <= endMs;
        }
      }

      return (
        matchesSearch &&
        matchesCM &&
        matchesClinic &&
        matchesType &&
        matchesDate
      );
    });

    // Sort
    if (sortColumn) {
      result.sort((a, b) => {
        let valA = a[sortColumn];
        let valB = b[sortColumn];

        if (sortColumn === "dateMs") {
          return sortDirection === "asc" ? valA - valB : valB - valA;
        }

        if (typeof valA === "number" && typeof valB === "number") {
          return sortDirection === "asc" ? valA - valB : valB - valA;
        }

        valA = valA ? valA.toString().toLowerCase() : "";
        valB = valB ? valB.toString().toLowerCase() : "";
        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [
    activities,
    search,
    cmFilter,
    clinicFilter,
    activityTypeFilter,
    dateRangeFilter,
    customStartDate,
    customEndDate,
    sortColumn,
    sortDirection,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / rowsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredAndSortedData.slice(start, start + rowsPerPage);
  }, [filteredAndSortedData, currentPage, rowsPerPage]);

  // Render Table Header with Sortable Icon
  const renderHeaderCell = (columnId, label, width, alignment = "left") => {
    const isSorted = sortColumn === columnId;
    return (
      <th
        onClick={() => handleSort(columnId)}
        style={{
          width,
          maxWidth: width,
          cursor: "pointer",
          userSelect: "none",
          textAlign: alignment,
        }}
        title={label}
      >
        <div
          className={`d-flex align-items-center gap-1 ${alignment === "center" ? "justify-content-center" : ""}`}
          style={{ fontWeight: "600", minWidth: 0 }}
        >
          <span className="cell-ellipsis">{label}</span>
          <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
            {isSorted ? (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  backgroundColor: "#27B99A",
                  marginLeft: "2px",
                }}
              >
                {sortDirection === "asc" ? (
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="#FFFFFF">
                    <path d="M4 1L7 4.5H5.5V7H2.5V4.5H1L4 1Z" />
                  </svg>
                ) : (
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="#FFFFFF">
                    <path d="M4 7L1 3.5H2.5V1H5.5V3.5H7L4 7Z" />
                  </svg>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  marginLeft: "2px",
                  opacity: 0.4,
                }}
              >
                <svg width="8" height="4" viewBox="0 0 8 4" fill="#CCCCCC">
                  <path d="M4 0L8 4H0L4 0Z" />
                </svg>
                <svg width="8" height="4" viewBox="0 0 8 4" fill="#CCCCCC">
                  <path d="M4 4L0 0L8 0L4 4Z" />
                </svg>
              </div>
            )}
          </span>
        </div>
      </th>
    );
  };

  // Step component for Patient Engagement Workflow
  const renderWorkflowTimeline = (activityType) => {
    const steps = [
      "Intake",
      "Initial Assessment",
      "Active Care",
      "Case Review",
      "Discharge",
    ];
    let currentStepIndex = 1; // Default to Initial Assessment

    if (activityType === "Initial Assessment") currentStepIndex = 1;
    else if (
      ["Follow-Up", "Care Coordination", "Contact Attempt"].includes(
        activityType,
      )
    )
      currentStepIndex = 2;
    else if (activityType === "Case Review") currentStepIndex = 3;

    return (
      <div style={{ margin: "24px 0" }}>
        <h5
          style={{
            fontSize: "11px",
            fontWeight: "700",
            color: "#8E8E93",
            letterSpacing: "0.8px",
            textTransform: "uppercase",
            marginBottom: "16px",
          }}
        >
          Patient Engagement Workflow
        </h5>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          {/* Connecting line */}
          <div
            style={{
              position: "absolute",
              top: "14px",
              left: "10px",
              right: "10px",
              height: "3px",
              backgroundColor: "#E2E8F0",
              zIndex: 1,
            }}
          />

          {/* Progress Fill */}
          <div
            style={{
              position: "absolute",
              top: "14px",
              left: "10px",
              width: `${(currentStepIndex / 4) * 95}%`,
              height: "3px",
              backgroundColor: "#27B99A",
              zIndex: 2,
              transition: "width 0.3s ease",
            }}
          />

          {steps.map((step, index) => {
            const isCompleted = index <= currentStepIndex;
            const isActive = index === currentStepIndex;

            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  zIndex: 3,
                  flex: 1,
                }}
              >
                <div
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "50%",
                    backgroundColor: isCompleted ? "#27B99A" : "#FFFFFF",
                    border: isCompleted ? "none" : "2.5px solid #CBD5E1",
                    color: isCompleted ? "#FFFFFF" : "#64748B",
                    fontWeight: "700",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: isActive
                      ? "0 0 0 4px rgba(39, 185, 154, 0.25)"
                      : "none",
                    transition: "all 0.2s ease",
                  }}
                >
                  {isCompleted && index < currentStepIndex ? (
                    <FaCheck size={9} />
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  style={{
                    fontSize: "9px",
                    fontWeight: isActive ? "700" : "500",
                    color: isActive ? "#064260" : "#8E8E93",
                    marginTop: "6px",
                    textAlign: "center",
                  }}
                >
                  {step}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      className="app-page cocm-activity-page"
      style={{ position: "relative" }}
    >
      <style>{`
                .cocm-activity-page { overflow-x: hidden; width: 100%; }
                .custom-dropdown-toggle::after { display: none !important; }

                thead th { 
                    position: sticky !important; 
                    top: 0; 
                    z-index: 10 !important; 
                    background-color: #064260 !important; 
                    color: white !important; 
                    border-bottom: none !important;
                }

                .table-container {
                    width: 100%;
                    max-height: calc(100vh - 280px);
                    overflow-x: hidden;
                    overflow-y: auto;
                }

                .custom-table {
                    border-collapse: separate;
                    border-spacing: 0;
                    width: 100%;
                    table-layout: fixed;
                }

                .custom-table th,
                .custom-table td {
                    border-bottom: 1px solid #EAECEF !important;
                    border-top: none !important;
                    vertical-align: middle !important;
                    padding: 12px 8px !important;
                    white-space: nowrap !important;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .custom-table .cell-ellipsis {
                    display: block;
                    max-width: 100%;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .custom-table .activity-pill {
                    display: inline-block;
                    max-width: 100%;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    vertical-align: middle;
                    box-sizing: border-box;
                }

                .custom-table tbody tr:hover td { 
                    background-color: #EBF3FF !important; 
                }

                .custom-table tbody tr {
                    transition: background-color 0.15s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideIn {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }
            `}</style>

      <div
        className="bg-white rounded shadow-sm"
        style={{ width: "100%", display: "flex", flexDirection: "column" }}
      >
        {/* ── Header & Filters Row (All in one line) ── */}
        <div
          className="p-4 d-flex justify-content-between align-items-center flex-wrap gap-3"
          style={{ borderBottom: "1px solid #E2E8F0" }}
        >
          <h2 className="app-page-title">
            {isCareManager ? "Activity" : "CoCM Activity"}
          </h2>

          {/* Filter Elements Row */}
          <div className="d-flex gap-3 align-items-center flex-wrap">
            {/* Search Box */}
            <div className="app-filter-search-wrap" style={{ width: "220px" }}>
              <FaSearch className="app-filter-search-icon" />
              <Form.Control
                type="text"
                className="app-filter-search"
                placeholder="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ width: "100%" }}
              />
            </div>

            {/* Care Manager Dropdown */}
            {!isCareManager && (
              <Dropdown
                onSelect={(val) => {
                  setCmFilter(val);
                  setCurrentPage(1);
                }}
              >
                <Dropdown.Toggle
                  className="custom-dropdown-toggle app-filter-dropdown"
                  style={{
                    width: "190px",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cmFilter}
                  </span>
                  <FaChevronDown
                    size={11}
                    style={{ opacity: 0.7, flexShrink: 0, marginLeft: "6px" }}
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{ maxHeight: "250px", overflowY: "auto" }}
                >
                  <Dropdown.Item eventKey="All Care Managers">
                    All Care Managers
                  </Dropdown.Item>
                  {careManagersList.map((cm) => (
                    <Dropdown.Item key={cm} eventKey={cm}>
                      {cm}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}

            {/* Activity Type Dropdown */}
            {!isCareManager && (
              <Dropdown
                onSelect={(val) => {
                  setActivityTypeFilter(val);
                  setCurrentPage(1);
                }}
              >
                <Dropdown.Toggle
                  className="custom-dropdown-toggle app-filter-dropdown"
                  style={{
                    width: "190px",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {activityTypeFilter}
                  </span>
                  <FaChevronDown
                    size={11}
                    style={{ opacity: 0.7, flexShrink: 0, marginLeft: "6px" }}
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item eventKey="All Activity Types">
                    All Activity Types
                  </Dropdown.Item>
                  <Dropdown.Item eventKey="Initial Assessment">
                    Initial Assessment
                  </Dropdown.Item>
                  <Dropdown.Item eventKey="Follow-Up">Follow-Up</Dropdown.Item>
                  <Dropdown.Item eventKey="Care Coordination">
                    Care Coordination
                  </Dropdown.Item>
                  <Dropdown.Item eventKey="Contact Attempt">
                    Contact Attempt
                  </Dropdown.Item>
                  <Dropdown.Item eventKey="Case Review">
                    Case Review
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}

            {/* Date Range Dropdown */}
            <Dropdown
              onSelect={(val) => {
                setDateRangeFilter(val);
                setCurrentPage(1);
              }}
            >
              <Dropdown.Toggle
                className="custom-dropdown-toggle app-filter-dropdown"
                style={{
                  width: "150px",
                }}
              >
                <span>
                  {dateRangeFilter === "All Time" && isCareManager
                    ? "Date"
                    : dateRangeFilter}
                </span>
                <FaCalendarAlt
                  size={13}
                  style={{ opacity: 0.7, marginLeft: "6px" }}
                />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="All Time">All Time</Dropdown.Item>
                <Dropdown.Item eventKey="Today">Today</Dropdown.Item>
                <Dropdown.Item eventKey="Last 7 Days">
                  Last 7 Days
                </Dropdown.Item>
                <Dropdown.Item eventKey="Last 30 Days">
                  Last 30 Days
                </Dropdown.Item>
                <Dropdown.Item eventKey="Custom Date">
                  Custom Date...
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Custom Date Range Picker Fields */}
            {dateRangeFilter === "Custom Date" && (
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="date"
                  value={customStartDate}
                  onChange={(e) => {
                    setCustomStartDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    backgroundColor: "#E9EEF1",
                    border: "none",
                    borderRadius: "8px",
                    height: "45px",
                    width: "145px",
                    fontSize: "13px",
                  }}
                />
                <span style={{ color: "#8E8E93" }}>to</span>
                <Form.Control
                  type="date"
                  value={customEndDate}
                  onChange={(e) => {
                    setCustomEndDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    backgroundColor: "#E9EEF1",
                    border: "none",
                    borderRadius: "8px",
                    height: "45px",
                    width: "145px",
                    fontSize: "13px",
                  }}
                />
                <Button
                  variant="link"
                  onClick={() => {
                    setCustomStartDate("");
                    setCustomEndDate("");
                  }}
                  style={{ color: "#EF4444", padding: 0 }}
                >
                  Clear
                </Button>
              </div>
            )}

            {/* Add Button for Care Manager */}
            {isCareManager && (
              <Dropdown onSelect={(val) => handleOpenAddDrawer(val)}>
                <Dropdown.Toggle
                  className="custom-dropdown-toggle"
                  style={{
                    backgroundColor: "#27B99A",
                    border: "none",
                    color: "#FFFFFF",
                    borderRadius: "8px",
                    height: "45px",
                    width: "120px",
                    fontSize: "14px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 16px",
                  }}
                >
                  <span>Add</span>
                  <FaChevronDown
                    size={11}
                    style={{ color: "#FFFFFF", marginLeft: "6px" }}
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Item eventKey="Initial Assessment">
                    Initial Assessment
                  </Dropdown.Item>
                  <Dropdown.Item eventKey="Follow-Up">Follow-Up</Dropdown.Item>
                  <Dropdown.Item eventKey="Care Coordination">
                    Care Coordination
                  </Dropdown.Item>
                  <Dropdown.Item eventKey="Case Review">
                    Case Review
                  </Dropdown.Item>
                  <Dropdown.Item eventKey="Contact Attempt">
                    Contact Attempt
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </div>

        {/* ── Table Container ── */}
        <div className=" flex-grow-1">
          <div className="table-container">
            <table className="table custom-table mb-0">
              <thead>
                <tr
                  style={{
                    height: "50px",
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                  }}
                >
                  {renderHeaderCell("patientName", "Name", "14%")}
                  {!isCareManager &&
                    renderHeaderCell("careManager", "Care Manager", "12%")}
                  {renderHeaderCell("dateMs", "Date", "9%")}
                  {renderHeaderCell("activityType", "Activity", "16%")}
                  {renderHeaderCell(
                    "duration",
                    "Duration (mins)",
                    "10%",
                    "center",
                  )}
                  {renderHeaderCell("phq9", "PHQ-9", "7%", "center")}
                  {renderHeaderCell("gad7", "GAD-7", "7%", "center")}
                  {renderHeaderCell("smfq", "SMFQ", "7%", "center")}
                  {renderHeaderCell("scared", "SCARED", "8%", "center")}
                  <th style={{ width: "10%", textAlign: "center" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody style={{ fontSize: "13px", color: "#444" }}>
                {loadingActivities ? (
                  <tr>
                    <td
                      colSpan={isCareManager ? 9 : 10}
                      className="text-center py-5 text-muted"
                    >
                      Loading activities...
                    </td>
                  </tr>
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isCareManager ? 9 : 10}
                      className="text-center py-5"
                    >
                      <div style={{ color: "#9CA3AF" }}>
                        <p
                          style={{
                            marginBottom: "4px",
                            fontWeight: 500,
                            color: "#6B7280",
                            fontSize: "15px",
                          }}
                        >
                          {activities.length === 0
                            ? "No activities yet"
                            : "No activities found matching filters"}
                        </p>
                        <p style={{ margin: 0, fontSize: "13px" }}>
                          {activities.length === 0 && isCareManager
                            ? "Click Add to log your first activity"
                            : "Try adjusting your search or date filter"}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((act) => {
                    const c = activityTypeStyles[act.activityType] || {
                      bg: "#E9EEF1",
                      color: "#064260",
                    };
                    return (
                      <tr key={act.id} style={{ height: "58px" }}>
                        <td
                          className="fw-semibold"
                          style={{ color: "#064260" }}
                          title={act.patientName || ""}
                        >
                          <span className="cell-ellipsis">
                            {act.patientName}
                          </span>
                        </td>
                        {!isCareManager && (
                          <td title={act.careManager || ""}>
                            <span className="cell-ellipsis">
                              {act.careManager}
                            </span>
                          </td>
                        )}
                        <td title={act.date || ""}>
                          <span className="cell-ellipsis">{act.date}</span>
                        </td>
                        <td title={act.activityType || ""}>
                          <span
                            className="activity-pill"
                            style={{
                              padding: "5px 10px",
                              borderRadius: "20px",
                              fontSize: "12px",
                              fontWeight: 600,
                              backgroundColor: c.bg,
                              color: c.color,
                            }}
                          >
                            {act.activityType}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {renderScoreCell(
                            act.duration === "-" ? "-" : act.duration,
                          )}
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            fontWeight: act.phq9 !== "-" ? "600" : "400",
                          }}
                        >
                          {renderScoreCell(act.phq9)}
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            fontWeight: act.gad7 !== "-" ? "600" : "400",
                          }}
                        >
                          {renderScoreCell(act.gad7)}
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            fontWeight: act.smfq !== "-" ? "600" : "400",
                          }}
                        >
                          {renderScoreCell(act.smfq)}
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            fontWeight: act.scared !== "-" ? "600" : "400",
                          }}
                        >
                          {renderScoreCell(act.scared)}
                        </td>

                        {/* Actions */}
                        <td style={{ textAlign: "center" }}>
                          <Button
                            className="app-view-btn"
                            onClick={() => handleOpenDrawer(act)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <TablePaginationBar
          currentPage={currentPage}
          totalItems={filteredAndSortedData.length}
          rowsPerPage={rowsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setCurrentPage(1);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          style={{ borderTop: "1px solid #E2E8F0" }}
        />
      </div>

      {/* ─── Add Activity Drawer (Care Coordination, Case Review, Contact Attempt, etc.) ─── */}
      {showAddDrawer &&
        selectedAddType &&
        ADD_DRAWER_CONFIG[selectedAddType] && (
          <div
            onClick={closeAddDrawer}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              zIndex: 1060,
              display: "flex",
              justifyContent: "flex-end",
              animation: "fadeIn 0.2s ease-out",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="activity-add-drawer"
              style={{
                width: "min(420px, 100vw)",
                height: "100%",
                backgroundColor: "#FFFFFF",
                boxShadow: "-8px 0 30px rgba(0, 0, 0, 0.12)",
                display: "flex",
                flexDirection: "column",
                borderTopLeftRadius: "12px",
                borderBottomLeftRadius: "12px",
                animation: "slideIn 0.25s ease-out",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "24px 28px 20px",
                  flexShrink: 0,
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "22px",
                    fontWeight: 700,
                    color: "#1F2937",
                  }}
                >
                  {editingActivityId
                    ? `Edit ${selectedAddType}`
                    : selectedAddType}
                </h2>
                <button
                  type="button"
                  onClick={closeAddDrawer}
                  style={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "6px",
                    backgroundColor: "#FFFFFF",
                    color: "#374151",
                    fontWeight: 600,
                    padding: "6px 16px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>

              {/* Drawer form */}
              <form
                onSubmit={handleSaveNewActivity}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "8px 28px 24px",
                  }}
                >
                  <div style={{ marginBottom: "20px" }}>
                    <label style={drawerFieldLabel}>Patient</label>
                    {editingActivityId ? (
                      <input
                        type="text"
                        disabled
                        value={
                          patients.find((p) => p.id === addPatientId)?.name ||
                          ""
                        }
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          fontSize: "14px",
                          border: "1px solid #D1D5DB",
                          borderRadius: "8px",
                          backgroundColor: "#F3F4F6",
                          color: "#6B7280",
                          cursor: "not-allowed",
                        }}
                      />
                    ) : (
                      <DrawerPatientSearch
                        patients={drawerPatients}
                        value={addPatientId}
                        onChange={setAddPatientId}
                      />
                    )}
                    {!editingActivityId &&
                      selectedAddType === "Follow-Up" &&
                      drawerPatients.length === 0 && (
                        <p
                          style={{
                            margin: "8px 0 0",
                            fontSize: "13px",
                            color: "#9CA3AF",
                          }}
                        >
                          No patients with a completed Initial Assessment yet.
                        </p>
                      )}
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <label style={drawerFieldLabel}>Date</label>
                    {editingActivityId ? (
                      <div
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          fontSize: "14px",
                          border: "1px solid #D1D5DB",
                          borderRadius: "8px",
                          backgroundColor: "#F3F4F6",
                          color: "#6B7280",
                          cursor: "not-allowed",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>{selectedActivity?.date}</span>
                        <FaCalendarAlt size={13} style={{ color: "#9CA3AF" }} />
                      </div>
                    ) : (
                      <DrawerDateInput value={addDate} onChange={setAddDate} />
                    )}
                  </div>

                  {ADD_DRAWER_CONFIG[selectedAddType].showDuration && (
                    <div style={{ marginBottom: "20px" }}>
                      <label style={drawerFieldLabel}>Duration (minutes)</label>
                      <input
                        type="number"
                        min="1"
                        value={addDuration}
                        onChange={(e) => setAddDuration(e.target.value)}
                        placeholder={
                          ADD_DRAWER_CONFIG[selectedAddType].durationPlaceholder
                        }
                        style={drawerInputStyle}
                      />
                    </div>
                  )}

                  <div style={{ marginBottom: "8px" }}>
                    <label style={drawerFieldLabel}>Notes</label>
                    <textarea
                      rows={5}
                      value={addNote}
                      onChange={(e) => setAddNote(e.target.value)}
                      placeholder={
                        ADD_DRAWER_CONFIG[selectedAddType].notesPlaceholder
                      }
                      style={{
                        ...drawerInputStyle,
                        resize: "vertical",
                        minHeight: "120px",
                        fontFamily: "Poppins, sans-serif",
                      }}
                    />
                  </div>
                </div>

                {/* Sticky footer */}
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    padding: "20px 28px",
                    borderTop: "1px solid #E5E7EB",
                    backgroundColor: "#FFFFFF",
                    flexShrink: 0,
                  }}
                >
                  <button
                    type="button"
                    onClick={closeAddDrawer}
                    style={{
                      flex: 1,
                      padding: "14px",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      backgroundColor: "#F9FAFB",
                      color: "#374151",
                      fontSize: "15px",
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addSaving}
                    style={{
                      flex: 1,
                      padding: "14px",
                      border: "none",
                      borderRadius: "8px",
                      backgroundColor: addSaving ? "#9CA3AF" : "#27B99A",
                      color: "#FFFFFF",
                      fontSize: "15px",
                      fontWeight: 600,
                      cursor: addSaving ? "not-allowed" : "pointer",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    {addSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      {/* ─── Detail Slide-out Drawer ─── */}
      {isDrawerOpen &&
        selectedActivity &&
        (isCareManager ? (
          /* Care Manager Details Drawer */
          <div
            onClick={() => setIsDrawerOpen(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              zIndex: 1050,
              display: "flex",
              justifyContent: "flex-end",
              animation: "fadeIn 0.2s ease-out",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "480px",
                height: "100%",
                backgroundColor: "#FFFFFF",
                boxShadow: "-5px 0 25px rgba(0, 0, 0, 0.15)",
                display: "flex",
                flexDirection: "column",
                padding: 0,
                animation: "slideIn 0.25s ease-out",
                fontFamily: "Poppins",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "32px 32px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  borderBottom: "1px solid #F1F5F9",
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "24px",
                      fontWeight: "700",
                      color: "#0F172A",
                      fontFamily: "Poppins",
                    }}
                  >
                    {selectedActivity.patientName}
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginTop: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        padding: "6px 14px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        backgroundColor:
                          activityTypeStyles[selectedActivity.activityType]
                            ?.bg || "#E9EEF1",
                        color:
                          activityTypeStyles[selectedActivity.activityType]
                            ?.color || "#064260",
                      }}
                    >
                      {selectedActivity.activityType}
                    </span>
                    <span
                      style={{
                        padding: "6px 14px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        backgroundColor: "#F1F5F9",
                        color: "#475569",
                      }}
                    >
                      {selectedActivity.date}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  style={{
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    color: "#0F172A",
                    fontWeight: "600",
                    padding: "8px 16px",
                    fontSize: "14px",
                    cursor: "pointer",
                    outline: "none",
                    transition: "all 0.15s ease",
                    fontFamily: "Poppins",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#F8FAFC";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#FFFFFF";
                  }}
                >
                  Close
                </button>
              </div>

              {/* Scrollable Content Area */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "32px",
                }}
              >
                {/* Details Section */}
                <div style={{ marginBottom: "32px" }}>
                  <h5
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "#94A3B8",
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                      marginBottom: "16px",
                    }}
                  >
                    Details
                  </h5>

                  {selectedActivity.activityType !== "Contact Attempt" && (
                    <div style={{ marginBottom: "20px" }}>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#64748B",
                          marginBottom: "6px",
                        }}
                      >
                        Duration (minutes)
                      </div>
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: "600",
                          color: "#0F172A",
                        }}
                      >
                        {selectedActivity.duration === "-"
                          ? "-"
                          : `${selectedActivity.duration}m`}
                      </div>
                    </div>
                  )}

                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#64748B",
                        marginBottom: "6px",
                      }}
                    >
                      Notes
                    </div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "400",
                        color: "#334155",
                        lineHeight: "1.6",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {selectedActivity.note || "—"}
                    </div>
                  </div>
                </div>

                {/* Assessment Summary Section */}
                {(() => {
                  const assessmentsToShow = [];
                  if (selectedActivity.phq9 && selectedActivity.phq9 !== "-") {
                    assessmentsToShow.push({
                      name: "PHQ-9",
                      score: selectedActivity.phq9,
                    });
                  }
                  if (selectedActivity.gad7 && selectedActivity.gad7 !== "-") {
                    assessmentsToShow.push({
                      name: "GAD-7",
                      score: selectedActivity.gad7,
                    });
                  }
                  if (selectedActivity.smfq && selectedActivity.smfq !== "-") {
                    assessmentsToShow.push({
                      name: "SMFQ",
                      score: selectedActivity.smfq,
                    });
                  }
                  if (
                    selectedActivity.scared &&
                    selectedActivity.scared !== "-"
                  ) {
                    assessmentsToShow.push({
                      name: "SCARED",
                      score: selectedActivity.scared,
                    });
                  }

                  if (assessmentsToShow.length === 0) return null;

                  const getCMSeverityStyle = (severityLabel) => {
                    const labelLower = (severityLabel || "").toLowerCase();
                    if (
                      labelLower.includes("minimal") ||
                      labelLower.includes("none")
                    ) {
                      return { bg: "#E0F2FE", color: "#0369A1" };
                    }
                    if (labelLower.includes("mild")) {
                      return { bg: "#FEF3C7", color: "#B45309" };
                    }
                    if (labelLower.includes("moderate")) {
                      return { bg: "#FFEDD5", color: "#C2410C" };
                    }
                    if (labelLower.includes("severe")) {
                      return { bg: "#FEE2E2", color: "#B91C1C" };
                    }
                    return { bg: "#F1F5F9", color: "#475569" };
                  };

                  return (
                    <div>
                      <hr
                        style={{
                          border: "0",
                          borderTop: "1px solid #F1F5F9",
                          margin: "32px 0",
                        }}
                      />
                      <h5
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#94A3B8",
                          letterSpacing: "1px",
                          textTransform: "uppercase",
                          marginBottom: "16px",
                        }}
                      >
                        Assessment Summary
                      </h5>
                      {assessmentsToShow.map((as, idx) => {
                        const severityObj = getScoreSeverity(as.name, as.score);
                        const severityLabel = severityObj?.label || "Moderate";
                        const severityStyle = getCMSeverityStyle(severityLabel);
                        return (
                          <div
                            key={idx}
                            style={{
                              border: "1px solid #E2E8F0",
                              borderRadius: "16px",
                              padding: "24px",
                              marginBottom: "16px",
                              backgroundColor: "#F8FAFC",
                            }}
                          >
                            <h4
                              style={{
                                fontSize: "16px",
                                fontWeight: "700",
                                color: "#0F172A",
                                marginBottom: "12px",
                                fontFamily: "Poppins",
                              }}
                            >
                              {as.name}
                            </h4>
                            <div
                              style={{
                                fontSize: "14px",
                                color: "#475569",
                                marginBottom: "8px",
                              }}
                            >
                              Score:{" "}
                              <span
                                style={{ color: "#0F172A", fontWeight: "600" }}
                              >
                                {as.score}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "14px",
                                color: "#475569",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              Severity:
                              <span
                                style={{
                                  padding: "2px 8px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  backgroundColor: severityStyle.bg,
                                  color: severityStyle.color,
                                }}
                              >
                                {severityLabel}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Sticky Footer Action Bar */}
              <div
                style={{
                  padding: "20px 32px",
                  borderTop: "1px solid #F1F5F9",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <button
                  onClick={handleEditClick}
                  style={{
                    width: "100%",
                    padding: "14px",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: "#27B99A",
                    color: "#FFFFFF",
                    fontSize: "15px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    fontFamily: "Poppins",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#219f84";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#27B99A";
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Admin / Standard Details Drawer */
          <div
            onClick={() => setIsDrawerOpen(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              zIndex: 1050,
              display: "flex",
              justifyContent: "flex-end",
              animation: "fadeIn 0.2s ease-out",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "480px",
                height: "100%",
                backgroundColor: "#FFFFFF",
                boxShadow: "-5px 0 25px rgba(0, 0, 0, 0.15)",
                display: "flex",
                flexDirection: "column",
                padding: "40px",
                overflowY: "auto",
                animation: "slideIn 0.25s ease-out",
                fontFamily: "Poppins",
              }}
            >
              {/* Drawer Header (Title, Badges and Close Button) */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#000000",
                      fontFamily: "Poppins",
                    }}
                  >
                    {selectedActivity.patientName}
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginTop: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        padding: "5px 14px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        backgroundColor:
                          activityTypeStyles[selectedActivity.activityType]
                            ?.bg || "#E9EEF1",
                        color:
                          activityTypeStyles[selectedActivity.activityType]
                            ?.color || "#064260",
                      }}
                    >
                      {selectedActivity.activityType}
                    </span>
                    <span
                      style={{
                        padding: "5px 14px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        backgroundColor: "#F1F5F9",
                        color: "#475569",
                      }}
                    >
                      {selectedActivity.date}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "6px",
                    backgroundColor: "#FFFFFF",
                    color: "#333333",
                    fontWeight: "600",
                    padding: "6px 16px",
                    fontSize: "13px",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  Close
                </button>
              </div>

              <hr
                style={{
                  border: "0",
                  borderTop: "1px solid #E2E8F0",
                  margin: "20px 0",
                }}
              />

              {/* Details Section */}
              <div style={{ marginBottom: "20px" }}>
                <h5
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#94A3B8",
                    letterSpacing: "0.8px",
                    textTransform: "uppercase",
                    marginBottom: "20px",
                  }}
                >
                  Details
                </h5>

                {selectedActivity.activityType !== "Contact Attempt" && (
                  <div style={{ marginBottom: "20px" }}>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#64748B",
                        marginBottom: "4px",
                      }}
                    >
                      Duration (minutes)
                    </div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "700",
                        color: "#0F172A",
                      }}
                    >
                      {selectedActivity.duration === "-"
                        ? "-"
                        : `${selectedActivity.duration}m`}
                    </div>
                  </div>
                )}

                <div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#64748B",
                      marginBottom: "4px",
                    }}
                  >
                    Notes
                  </div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "500",
                      color: "#0F172A",
                      lineHeight: "1.5",
                    }}
                  >
                    {selectedActivity.note || "—"}
                  </div>
                </div>
              </div>

              {/* Assessment Summary Section (Only for Follow-Up and Initial Assessment) */}
              {(selectedActivity.activityType === "Follow-Up" ||
                selectedActivity.activityType === "Initial Assessment") && (
                <>
                  <hr
                    style={{
                      border: "0",
                      borderTop: "1px solid #E2E8F0",
                      margin: "20px 0",
                    }}
                  />
                  <div>
                    <h5
                      style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        color: "#94A3B8",
                        letterSpacing: "0.8px",
                        textTransform: "uppercase",
                        marginBottom: "20px",
                      }}
                    >
                      Assessment Summary
                    </h5>

                    {/* PHQ-9 Card */}
                    <div
                      style={{
                        border: "1px solid #E2E8F0",
                        borderRadius: "16px",
                        padding: "24px",
                        marginBottom: "16px",
                        backgroundColor: "#FFFFFF",
                      }}
                    >
                      <h4
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#0F172A",
                          marginBottom: "12px",
                        }}
                      >
                        PHQ-9
                      </h4>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#475569",
                          marginBottom: "6px",
                        }}
                      >
                        Score:{" "}
                        <span style={{ color: "#0F172A", fontWeight: "700" }}>
                          {selectedActivity.phq9}
                        </span>
                      </div>
                      <div style={{ fontSize: "14px", color: "#475569" }}>
                        Severity:{" "}
                        <span style={{ color: "#0F172A", fontWeight: "700" }}>
                          {getScoreSeverity("PHQ-9", selectedActivity.phq9)
                            ?.label || "Moderate"}
                        </span>
                      </div>
                    </div>

                    {/* GAD-7 Card */}
                    <div
                      style={{
                        border: "1px solid #E2E8F0",
                        borderRadius: "16px",
                        padding: "24px",
                        backgroundColor: "#FFFFFF",
                      }}
                    >
                      <h4
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#0F172A",
                          marginBottom: "12px",
                        }}
                      >
                        GAD-7
                      </h4>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#475569",
                          marginBottom: "6px",
                        }}
                      >
                        Score:{" "}
                        <span style={{ color: "#0F172A", fontWeight: "700" }}>
                          {selectedActivity.gad7}
                        </span>
                      </div>
                      <div style={{ fontSize: "14px", color: "#475569" }}>
                        Severity:{" "}
                        <span style={{ color: "#0F172A", fontWeight: "700" }}>
                          {getScoreSeverity("GAD-7", selectedActivity.gad7)
                            ?.label || "Moderate"}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
    </div>
  );
};

export default ActivityCoCM;
